
export function createTimeIndex(intervals: { start: number, end: number }[]) {
    // intervals are sorted and disjoint
    // Precompute cumulative effective time
    const index: { end: number, accum: number }[] = [];
    let accum = 0;

    for (const iv of intervals) {
        const duration = iv.end - iv.start;
        accum += duration;
        index.push({ end: iv.end, accum: accum });
    }

    const maxAccum = accum;

    const getAccumulatedTimeAt = (t: number) => {
        if (intervals.length === 0) return 0;
        if (t < intervals[0].start) return 0;
        if (t >= intervals[intervals.length - 1].end) return maxAccum;

        // Binary Search for interval ending after t or at t
        let low = 0, high = intervals.length - 1;
        let idx = -1;

        while (low <= high) {
            const mid = (low + high) >>> 1;
            if (intervals[mid].end > t) {
                idx = mid;
                high = mid - 1;
            } else {
                low = mid + 1;
            }
        }

        if (idx === -1) return maxAccum; // Should not happen given check above

        const iv = intervals[idx];
        // Preceding total
        const prevAccum = idx > 0 ? index[idx - 1].accum : 0;

        if (t < iv.start) {
            // In gap before this interval -> return accumulated up to start
            // which is equal to prevAccum
            return prevAccum;
        } else {
            // Inside interval
            return prevAccum + (t - iv.start);
        }
    };

    return { getAccumulatedTimeAt, maxAccum };
}

export function calculateStatusStatsMainThread(
    statusTimelines: Record<number, { start: number, end: number, max?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number }[]>,
    statusStarts: Record<number, { time: number, max?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number }>,
    effectiveIntervals: { start: number, end: number }[],
    effectiveTime: number,
    startTime: number,
    endTime: number,
    trackedStatusIds: number[],
    condNameMap: Record<number, string>
) {
    // 1. Build Time Index once (O(W))
    const { getAccumulatedTimeAt } = createTimeIndex(effectiveIntervals);

    // Precise denominators based on the clipping range [startTime, endTime]
    const realEffTime = getAccumulatedTimeAt(endTime) - getAccumulatedTimeAt(startTime);
    const realTotalTime = endTime - startTime;

    const totalTime = Math.max(0.1, realTotalTime);
    const effTime = Math.max(0.1, realEffTime);

    // Merge transient "Active" statuses into timelines
    // (This block was removed because it was unused and mutated input unexpectedly)

    const results: any[] = [];

    // We iterate distinct Status IDs
    const allIds = new Set<number>();

    // Optimization: If processing tracked statuses only, iterate those instead of EVERYTHING
    if (trackedStatusIds && trackedStatusIds.length > 0) {
        for (const id of trackedStatusIds) {
            allIds.add(id);
        }
    } else {
        // Fallback: If no tracking list (rare?), iterate all found data
        Object.keys(statusTimelines).forEach(k => allIds.add(+k));
        Object.keys(statusStarts).forEach(k => allIds.add(+k));
    }

    for (const id of allIds) {
        if (trackedStatusIds && trackedStatusIds.length > 0 && !trackedStatusIds.includes(id)) continue;

        // Skip if this tracked status has no data at all (saves processing)
        if (!statusTimelines[id] && !statusStarts[id]) continue;

        // Construct full timeline for this status
        const rawIntervals = [...(statusTimelines[id] || [])];
        if (statusStarts[id]) {
            rawIntervals.push({ 
                start: statusStarts[id].time, end: Number.MAX_VALUE, max: statusStarts[id].max,
                magicAttack: statusStarts[id].magicAttack, alchemicDamage: statusStarts[id].alchemicDamage,
                magicCastingSpeed: statusStarts[id].magicCastingSpeed, alchemyCastingSpeed: statusStarts[id].alchemyCastingSpeed
            });
        }

        // Clip to [startTime, endTime] ?
        // Usually statuses outside combat don't matter?
        // But user might want to see them.
        // Let's clip to endTime effectively for Uptime calculation.

        // 1. Merge Overlaps
        rawIntervals.sort((a, b) => a.start - b.start);

        const merged: { start: number, end: number, max?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number }[] = [];
        let maxOfAll = 0;
        let maxMagicAttack = 0;
        let maxAlchemicDamage = 0;
        let maxMagicCasting = 0;
        let maxAlchemyCasting = 0;

        if (rawIntervals.length > 0) {
            let prev = { ...rawIntervals[0] };
            if (rawIntervals[0].max && rawIntervals[0].max > maxOfAll) maxOfAll = rawIntervals[0].max;
            if (rawIntervals[0].magicAttack && rawIntervals[0].magicAttack > maxMagicAttack) maxMagicAttack = rawIntervals[0].magicAttack;
            if (rawIntervals[0].alchemicDamage && rawIntervals[0].alchemicDamage > maxAlchemicDamage) maxAlchemicDamage = rawIntervals[0].alchemicDamage;
            if (rawIntervals[0].magicCastingSpeed && rawIntervals[0].magicCastingSpeed > maxMagicCasting) maxMagicCasting = rawIntervals[0].magicCastingSpeed;
            if (rawIntervals[0].alchemyCastingSpeed && rawIntervals[0].alchemyCastingSpeed > maxAlchemyCasting) maxAlchemyCasting = rawIntervals[0].alchemyCastingSpeed;

            for (let i = 1; i < rawIntervals.length; i++) {
                const curr = rawIntervals[i];
                if (curr.max && curr.max > maxOfAll) maxOfAll = curr.max;
                if (curr.magicAttack && curr.magicAttack > maxMagicAttack) maxMagicAttack = curr.magicAttack;
                if (curr.alchemicDamage && curr.alchemicDamage > maxAlchemicDamage) maxAlchemicDamage = curr.alchemicDamage;
                if (curr.magicCastingSpeed && curr.magicCastingSpeed > maxMagicCasting) maxMagicCasting = curr.magicCastingSpeed;
                if (curr.alchemyCastingSpeed && curr.alchemyCastingSpeed > maxAlchemyCasting) maxAlchemyCasting = curr.alchemyCastingSpeed;

                // Gap tolerance: treat intervals within 2s as continuous (buff refresh cycles)
                const MERGE_GAP_TOLERANCE = 2.0;
                if (curr.start <= prev.end + MERGE_GAP_TOLERANCE) {
                    prev.end = Math.max(prev.end, curr.end);
                    if (curr.max && (!prev.max || curr.max > prev.max)) { prev.max = curr.max; }
                    if (curr.magicAttack && (!prev.magicAttack || curr.magicAttack > prev.magicAttack)) { prev.magicAttack = curr.magicAttack; }
                    if (curr.alchemicDamage && (!prev.alchemicDamage || curr.alchemicDamage > prev.alchemicDamage)) { prev.alchemicDamage = curr.alchemicDamage; }
                    if (curr.magicCastingSpeed && (!prev.magicCastingSpeed || curr.magicCastingSpeed > prev.magicCastingSpeed)) { prev.magicCastingSpeed = curr.magicCastingSpeed; }
                    if (curr.alchemyCastingSpeed && (!prev.alchemyCastingSpeed || curr.alchemyCastingSpeed > prev.alchemyCastingSpeed)) { prev.alchemyCastingSpeed = curr.alchemyCastingSpeed; }
                } else {
                    merged.push(prev);
                    prev = { ...curr };
                }
            }
            merged.push(prev);
        }

        let totalUptime = 0;
        let effectiveUptime = 0;

        for (const m of merged) {
            // Clip to [startTime, endTime]
            const start = Math.max(m.start, startTime);
            const end = Math.min(m.end, endTime);

            if (end > start) {
                totalUptime += (end - start);

                // Effective Uptime using Global Clock
                // The clock function getAccumulatedTimeAt already handles clipping internally
                const effStart = getAccumulatedTimeAt(start);
                const effEnd = getAccumulatedTimeAt(end);
                effectiveUptime += (effEnd - effStart);
            }
        }

        // Final snapping and hierarchy enforcement
        let effRatio = Math.min(1.0, effectiveUptime / effTime);
        let allRatio = Math.min(1.0, totalUptime / totalTime);

        // Precision Snapping: If within 0.1% of 100%, snap to 100% 
        // to handle floating point drift from sub-second clipping.
        if (effRatio > 0.999) effRatio = 1.0;
        if (allRatio > 0.999) allRatio = 1.0;

        // Mathematical Hierarchy: Effective Coverage and Overall Coverage are distinct.
        // A status applied heavily during non-effective bounds (down time) CAN result
        // in Overall% > Effective%. We simply let the numbers represent their true ranges.
        // Removed flawed constraint that forced allRatio <= effRatio.

        results.push({
            id: id,
            name: condNameMap?.[id] || `Status ${id}`,
            uptime: totalUptime,
            effectiveUptime: effectiveUptime,
            uptimeRatio: effRatio,
            overallRatio: allRatio,
            maxPercent: maxOfAll > 0 ? maxOfAll : undefined,
            magicAttack: maxMagicAttack > 0 ? maxMagicAttack : undefined,
            alchemicDamage: maxAlchemicDamage > 0 ? maxAlchemicDamage : undefined,
            magicCastingSpeed: maxMagicCasting > 0 ? maxMagicCasting : undefined,
            alchemyCastingSpeed: maxAlchemyCasting > 0 ? maxAlchemyCasting : undefined
        });
    }

    return results.sort((a, b) => {
        if (Math.abs(b.uptimeRatio - a.uptimeRatio) > 0.0001) {
            return b.uptimeRatio - a.uptimeRatio;
        }
        return a.name.localeCompare(b.name);
    });
}
